/**
 * Payment-Link Link-Tester — Stage-Map (Spec 501, Phase 3).
 *
 * Bildet den aktuellen Panel-Host (`admin.<stage>.facade…`) auf die Payment-Link-
 * Basis-URL (`pay.<stage>.facade…`) ab. Anders als die Devportal-Stage-Map hat
 * diese Map einen PROD-Slot. Alle 4 Hosts sind Facade-bestaetigt seit 2026-08-01
 * (Handover 2026-08-01_info_link-generierung-stage-hosts-und-ui-hinweise.md).
 * Die Prod-Copy-Sperre (`placeholder:true`) ist seit Issue #519 aufgehoben
 * (Owner-Go 2026-08-02, Dienst auf prod live) — der Mechanismus bleibt als
 * Kill-Switch erhalten, falls eine Basis je wieder gesperrt werden muss.
 */

export type PayStage = 'dev' | 'testing' | 'staging' | 'prod';

export interface PayStageEntry {
  stage: PayStage;
  /** Payment-Link-Basis-URL fuer diese Stage. */
  payBaseUrl: string;
  /**
   * `true` = die UI zeigt den Wert als markierten Platzhalter und sperrt den Copy
   * des Gesamtlinks. Aktuell fuehrt kein Eintrag das Flag (Prod entsperrt mit
   * Issue #519); es bleibt als Kill-Switch fuer unbestaetigte Basen erhalten.
   */
  placeholder?: boolean;
}

/**
 * Panel-Host → Payment-Link-Stage. Prod-Basis folgt dem Prod-API-Muster OHNE
 * Stage-Segment (`pay.facade…`, analog `api.facade…`) — NICHT per String-Template
 * ableiten. Facade-bestaetigt 2026-08-01 (staging + prod live seit 2026-08-01).
 */
export const PAY_STAGE_BASE_URLS: Readonly<Record<string, PayStageEntry>> = {
  'admin.dev.facade.dcb.vasp.kanzaroo.com': {
    stage: 'dev',
    payBaseUrl: 'https://pay.dev.facade.dcb.vasp.kanzaroo.com',
  },
  'admin.testing.facade.dcb.vasp.kanzaroo.com': {
    stage: 'testing',
    payBaseUrl: 'https://pay.testing.facade.dcb.vasp.kanzaroo.com',
  },
  'admin.staging.facade.dcb.vasp.kanzaroo.com': {
    stage: 'staging',
    payBaseUrl: 'https://pay.staging.facade.dcb.vasp.kanzaroo.com',
  },
  'admin.facade.dcb.vasp.kanzaroo.com': {
    stage: 'prod',
    payBaseUrl: 'https://pay.facade.dcb.vasp.kanzaroo.com',
  },
};

/**
 * Default fuer unbekannte Hosts (localhost/lokale Entwicklung): Testing — eine
 * funktionierende Test-Basis ist dort nuetzlicher als ein Platzhalter (analog
 * Devportal-Stage-Map).
 */
export const DEFAULT_PAY_STAGE: PayStageEntry =
  PAY_STAGE_BASE_URLS['admin.testing.facade.dcb.vasp.kanzaroo.com'];

/** Panel-Host → Stage-Eintrag (Basis-URL + Placeholder-Flag). */
export function resolvePayBaseUrl(hostname: string): PayStageEntry {
  return PAY_STAGE_BASE_URLS[hostname] ?? DEFAULT_PAY_STAGE;
}

/**
 * Ist dieser Eintrag eine unbestaetigte Platzhalter-Basis (→ nicht-kopierbar)?
 * Seit Issue #519 traegt kein Eintrag mehr das Flag; die Funktion bleibt der
 * eine Pruefpunkt, falls eine Basis wieder gesperrt werden muss.
 */
export function isPlaceholderProd(entry: PayStageEntry): boolean {
  return entry.placeholder === true;
}

/**
 * Stage-Eintrag für eine bekannte Payment-Link-Stage. Für den korrigierten Link in
 * Modus B: er soll auf den Host der EINGEFÜGTEN URL bauen, nicht auf die Panel-Stage
 * (Integrations-Review M2). `DEFAULT_PAY_STAGE` als Rückfall.
 */
export function payStageEntry(stage: PayStage): PayStageEntry {
  for (const entry of Object.values(PAY_STAGE_BASE_URLS)) {
    if (entry.stage === stage) {
      return entry;
    }
  }
  return DEFAULT_PAY_STAGE;
}

/**
 * Zu welcher Payment-Link-Stage gehoert eine (eingefuegte) URL? Fuer die
 * Fremd-Stage-Erkennung in Modus B. `null` = keiner bekannten Stage zuordenbar
 * oder nicht parsebar.
 */
export function detectStageFromUrl(rawUrl: string): PayStage | null {
  let origin: string;
  try {
    origin = new URL(rawUrl).origin;
  } catch {
    return null;
  }
  for (const entry of Object.values(PAY_STAGE_BASE_URLS)) {
    if (new URL(entry.payBaseUrl).origin === origin) {
      return entry.stage;
    }
  }
  return null;
}
